/**
 * sentence-stream.js — helper for streaming TTS.
 *
 * Splits an ever-growing buffer of streamed LLM tokens into complete sentences
 * so each can be sent to TTS as soon as it's ready (instead of waiting for the
 * full reply). This is what makes voice mode feel *responsive* — Rammy starts
 * speaking sentence 1 while the LLM is still generating sentence 3.
 *
 * Rules of thumb (kept deliberately simple — this is best-effort chunking):
 *   • A sentence ends at `. ! ? …` optionally followed by a closing quote/bracket,
 *     then whitespace, then a NEW-sentence starter (uppercase letter, digit,
 *     opening quote/bracket). This filters out abbreviations ("e.g. this")
 *     and mid-sentence ellipses ("I'm... thinking.").
 *   • Newlines also fire, even without punctuation — model paragraph breaks
 *     are natural TTS boundaries.
 *   • Sentences shorter than 3 characters are held back and merged with the
 *     next one.
 *   • End-of-buffer WITHOUT trailing whitespace isn't matched here — the
 *     caller flushes the tail via a separate `endStream()` call once the LLM
 *     stream closes.
 */

const SENTENCE_END = /([.!?…]+["')\]]?)(\s+(?=[A-Z"'([{0-9])|\n+)/g;
const MIN_CHUNK_CHARS = 3;

/**
 * Feed the running buffer; get back complete sentences and the remainder.
 *
 * @param {string} buffer - everything received so far (or since last drain)
 * @returns {{ sentences: string[], remainder: string }}
 */
export function extractSentences(buffer) {
  const sentences = [];
  let cursor = 0;
  let m;
  const re = new RegExp(SENTENCE_END.source, "g");
  while ((m = re.exec(buffer)) !== null) {
    const end = m.index + m[0].length;
    const chunk = buffer.slice(cursor, end).trim();
    if (chunk.length >= MIN_CHUNK_CHARS) {
      sentences.push(chunk);
      cursor = end;
    }
  }
  return { sentences, remainder: buffer.slice(cursor) };
}
